import React from 'react';
import { useUser } from '../contexts/UserContext';
import { hasModuleAccess, MODULES } from '../utils/simplePermissions';
import { useNavigate } from 'react-router-dom';
import { fmtTime, fmtDateShort, fmtDate, fmtDuration } from '../utils/dateFormatters';
import { useQueryClient } from '@tanstack/react-query';
import { useMyDashboardData } from '../hooks/useMyDashboardData';
import {
  ClockIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  UserIcon,
  MapPinIcon,
  BanknotesIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline';
import ModernPageHeader, { ModernStatCard, ModernActionButton } from '../components/Common/ModernPageHeader';
import ModernCard, { ModernCardHeader, ModernCardBody } from '../components/Common/ModernCard';
import '../styles/modern-enhancements.css';
import { formatCustomerAddress } from '../utils/customerAddress';
import { getRoleDisplayName } from '../utils/roleUtils';
import { formatCurrency } from '../utils/formatters';

export default function MyDashboard(){
  const { user } = useUser();
  // Board #502. This dashboard is the EMPLOYEE's landing page, so it is the one most likely to be
  // seen by somebody without a module — and it was offering two routes into /calendar, which
  // SimplePermissionRoute then refuses with Access Restricted. A control that leads somewhere the
  // app will refuse is worse than no control: it reads as the app being broken rather than as the
  // feature being off. Checked with the same hasModuleAccess the route itself uses.
  const canSeeCalendar = hasModuleAccess(user, MODULES.CALENDAR);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Use React Query hook
  const { data: dashboardData, isLoading: loading } = useMyDashboardData(user?.company_id, user?.id);

  // Destructure data from hook
  const weeklyHours = dashboardData?.weeklyHours ?? 0;
  const nextJob = dashboardData?.nextJob ?? null;
  // null = the read was refused (a Crew seat cannot read expenses); shown as a dash, not $0.
  const owedToMe = dashboardData ? (dashboardData.owedToMe ?? null) : [];
  const owedCount = owedToMe ? owedToMe.length : null;
  const owedAmount = owedToMe ? owedToMe.reduce((sum, e) => sum + (Number(e.amount) || 0), 0) : null;
  const upcomingJobs = dashboardData?.upcomingJobs ?? [];
  const todayJobs = dashboardData?.todayJobs ?? [];
  const recentActivity = dashboardData?.recentActivity ?? [];

  const activeJob = todayJobs.find((job) => job.status?.toLowerCase() === 'in_progress') || null;
  const remainingTodayJobs = todayJobs.filter((job) => job.id !== activeJob?.id);
  const completedTodayCount = todayJobs.filter((job) => job.status?.toLowerCase() === 'completed').length;
  const nextTodayJob = activeJob || remainingTodayJobs[0] || null;

  const formatJobTime = (job) => {
    if (!job?.scheduled_start) return 'TBD';
    const start = fmtTime(new Date(job.scheduled_start));
    if (!job.scheduled_end) return start;
    return `${start} - ${fmtTime(new Date(job.scheduled_end))}`;
  };

  // Status Chip Component
  const StatusChip = ({ status, type = 'default' }) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      submitted: 'bg-blue-100 text-blue-800 border-blue-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      scheduled: 'bg-purple-100 text-purple-800 border-purple-200',
      in_progress: 'bg-orange-100 text-orange-800 border-orange-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      default: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${colors[status] || colors.default}`}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  // Primary KPI stats row
  const primaryStats = [
    {
      title: 'Hours This Week',
      value: loading ? '—' : `${weeklyHours}h`,
      icon: ClockIcon,
      onClick: () => navigate('/my/time')
    },
    {
      title: 'Next Job',
      value: loading ? '—' : (nextJob ? (nextJob.scheduled_start ? fmtDateShort(nextJob.scheduled_start) : 'TBD') : 'None'),
      icon: CalendarDaysIcon,
      onClick: () => navigate('/jobs')
    },
    {
      // #6057: expenses have no approval step (P29), so "Pending" was only the column default.
      // Same predicate as the Expenses page's "Owed" filter, narrowed to me; the click opens it.
      title: 'Owed to me',
      value: loading || owedAmount === null ? '—' : formatCurrency(owedAmount),
      change: loading || owedCount === null ? undefined : `${owedCount} expense${owedCount === 1 ? '' : 's'}`,
      icon: BanknotesIcon,
      onClick: () => navigate(`/expenses?reimbursable=yes&employee=${encodeURIComponent(user?.id || '')}`)
    }
  ];

  return (
    <div className="p-6 relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 -right-16 w-96 h-96 bg-gradient-to-br from-purple-300/40 to-blue-300/30 rounded-full blur-3xl"></div>
        <div className="absolute top-64 -left-20 w-96 h-96 bg-gradient-to-br from-emerald-300/30 to-teal-300/20 rounded-full blur-3xl"></div>
      </div>
      <ModernPageHeader
        compact
        title="My Dashboard"
        subtitle="Your personal workspace"
        icon={UserIcon}
        gradient="indigo"
        stats={[
          { label: 'Hours', value: loading ? '—' : `${weeklyHours}h` },
          { label: 'Today', value: loading ? '—' : todayJobs.length },
          { label: 'Owed to me', value: loading || owedAmount === null ? '—' : formatCurrency(owedAmount) }
        ]}
      />

      {/* KPI Row */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {primaryStats.map((stat, index) => (
            <ModernStatCard key={index} {...stat} gradient={['indigo','purple','orange'][index % 3]} />
          ))}
        </div>
      </div>

      {/* Today */}
      <ModernCard className="mb-8 hover-glow">
        <ModernCardHeader
          title="Today"
          icon={CalendarDaysIcon}
          gradient="green"
          action={canSeeCalendar ? (
            <button onClick={() => navigate('/calendar')} className="text-sm text-green-100 hover:text-white font-medium">
              View Schedule
            </button>
          ) : null}
        />
        <ModernCardBody>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : todayJobs.length === 0 ? (
            <div className="text-center py-8">
              <CalendarDaysIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <div className="text-white text-sm font-medium">No jobs scheduled today</div>
              {canSeeCalendar && (
                <button
                  onClick={() => navigate('/calendar')}
                  className="mt-2 text-green-200 hover:text-white text-sm font-medium"
                >
                  View this week
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-white/15 bg-white/10 p-3 text-center">
                  <div className="text-2xl font-bold text-white">{todayJobs.length}</div>
                  <div className="text-xs text-gray-200">Scheduled</div>
                </div>
                <div className="rounded-lg border border-white/15 bg-white/10 p-3 text-center">
                  <div className="text-2xl font-bold text-white">{activeJob ? 1 : 0}</div>
                  <div className="text-xs text-gray-200">Active</div>
                </div>
                <div className="rounded-lg border border-white/15 bg-white/10 p-3 text-center">
                  <div className="text-2xl font-bold text-white">{completedTodayCount}</div>
                  <div className="text-xs text-gray-200">Completed</div>
                </div>
              </div>

              {nextTodayJob && (
                <div
                  className={`rounded-xl border p-4 ${
                    activeJob
                      ? 'border-orange-300/60 bg-orange-500/15'
                      : 'border-green-300/40 bg-green-500/10'
                  }`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <StatusChip status={nextTodayJob.status?.toLowerCase()} />
                        <span className="text-xs font-medium text-gray-200">{formatJobTime(nextTodayJob)}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-white truncate">{nextTodayJob.title || 'Service Call'}</h3>
                      <div className="mt-1 text-sm text-gray-200">{nextTodayJob.customers?.name || 'Customer'}</div>
                      {formatCustomerAddress(nextTodayJob.customers) && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-gray-300">
                          <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{formatCustomerAddress(nextTodayJob.customers)}</span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => navigate(`/field-job/${nextTodayJob.id}`)}
                      className="w-full sm:w-auto px-4 py-2 rounded-lg bg-white/95 text-gray-900 text-sm font-medium hover:bg-gray-100 transition-colors"
                    >
                      Open Job
                    </button>
                  </div>
                </div>
              )}

              {remainingTodayJobs.length > 0 && (
                <div className="space-y-2">
                  {remainingTodayJobs.map(job => (
                    <button
                      key={job.id}
                      type="button"
                      onClick={() => navigate(`/field-job/${job.id}`)}
                      className="w-full rounded-lg border border-white/10 bg-white/5 p-3 text-left hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-white truncate">{job.title || 'Service Call'}</div>
                          <div className="text-xs text-gray-300 truncate">{job.customers?.name || 'Customer'}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-xs font-medium text-gray-200">{formatJobTime(job)}</div>
                          <StatusChip status={job.status?.toLowerCase()} />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </ModernCardBody>
      </ModernCard>

      {/* My Actions */}
      <ModernCard className="mb-8 hover-glow">
        <ModernCardHeader title="My Actions" gradient="gray" />
        <ModernCardBody>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <ModernActionButton icon={ClockIcon} onClick={() => navigate('/my/time')}>Submit Time Entry</ModernActionButton>
            <ModernActionButton variant="warning" icon={BanknotesIcon} onClick={() => navigate('/expenses?new=expense')}>Submit Expense</ModernActionButton>
            <ModernActionButton variant="info" icon={ArchiveBoxIcon} onClick={() => navigate('/operations/inventory')}>View Inventory</ModernActionButton>
          </div>
        </ModernCardBody>
      </ModernCard>

      {/* Timeline/Feed Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Jobs */}
        <ModernCard className="hover-glow">
          <ModernCardHeader
            title="Upcoming Jobs"
            icon={CalendarDaysIcon}
            gradient="green"
            action={(
              <button onClick={() => navigate('/jobs')} className="text-sm text-white hover:text-white/80 font-medium">
                View All
              </button>
            )}
          />
          <ModernCardBody>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : upcomingJobs.length === 0 ? (
              <div className="text-center py-8">
                <CalendarDaysIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <div className="text-gray-500 text-sm">No upcoming jobs scheduled</div>
                <button
                  onClick={() => navigate('/calendar')}
                  className="mt-2 text-green-700 hover:text-green-800 text-sm font-medium"
                >
                  View your schedule
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingJobs.map(job => (
                  <div 
                    key={job.id} 
                    className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors cursor-pointer"
                    onClick={() => navigate(`/field-job/${job.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{job.title || 'Service Call'}</h4>
                          <StatusChip status={job.status?.toLowerCase()} />
                        </div>
                        <div className="text-sm text-gray-600 mb-2">
                          {job.customers?.name || 'Customer'}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <CalendarDaysIcon className="w-4 h-4" />
                            {job.scheduled_start ? (() => {
                              const d = new Date(job.scheduled_start);
                              const now = new Date();
                              const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
                              const isToday = d.toDateString() === now.toDateString();
                              const isTomorrow = d.toDateString() === tomorrow.toDateString();
                              const date = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : fmtDateShort(d);
                              return `${date} at ${fmtTime(d)}`;
                            })() : 'TBD'}
                          </div>
                          {(job.scheduled_start && job.scheduled_end) ? (
                            <div className="flex items-center gap-1">
                              <ClockIcon className="w-4 h-4" />
                              {fmtDuration(Math.round((new Date(job.scheduled_end) - new Date(job.scheduled_start)) / 60000))}
                            </div>
                          ) : job.estimated_duration ? (
                            <div className="flex items-center gap-1">
                              <ClockIcon className="w-4 h-4" />
                              {job.estimated_duration >= 1 ? `${job.estimated_duration}h` : `${Math.round(job.estimated_duration * 60)}m`}
                            </div>
                          ) : null}
                          {formatCustomerAddress(job.customers) && (
                            <div className="flex items-center gap-1">
                              <MapPinIcon className="w-4 h-4" />
                              {formatCustomerAddress(job.customers)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ModernCardBody>
        </ModernCard>

        {/* Recent Activity */}
        <ModernCard className="hover-glow">
          <ModernCardHeader
            title="Recent Activity"
            icon={DocumentTextIcon}
            gradient="indigo"
            action={(
              <button onClick={() => navigate('/profile')} className="text-sm text-white hover:text-white/80 font-medium">
                View Profile
              </button>
            )}
          />
          <ModernCardBody>
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="animate-pulse flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="text-center py-8">
                <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <div className="text-gray-500 text-sm">No recent activity on your jobs</div>
                <div className="text-gray-400 text-xs mt-1">Your assigned work orders, your quotes, and payments on your jobs will appear here</div>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map(activity => (
                  <div
                    key={activity.id}
                    onClick={() => {
                      // #4832: the hook tags scheduled/completed rows 'work_order' and
                      // sent/changes_requested rows 'quote' (useMyDashboardData fetchRecentActivity);
                      // both used to open the quote editor.
                      if (activity.type === 'work_order') {
                        navigate(`/jobs/${activity.entityId}`);
                      } else if (activity.type === 'quote') {
                        navigate(`/quotes?edit=${activity.entityId}`);
                      }
                      // Payments don't have a detail page yet
                    }}
                    className={`flex items-center gap-3 p-3 bg-gray-50 rounded-lg ${
                      (activity.type === 'work_order' || activity.type === 'quote') ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''
                    }`}
                  >
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        {activity.icon ? (
                          <activity.icon className="w-4 h-4 text-blue-600" />
                        ) : activity.type === 'payment' ? (
                          <BanknotesIcon className="w-4 h-4 text-blue-600" />
                        ) : activity.type === 'quote' ? (
                          <DocumentTextIcon className="w-4 h-4 text-blue-600" />
                        ) : (
                          <CalendarDaysIcon className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900">{activity.message}</div>
                      <div className="text-xs text-gray-500">{fmtDate(activity.time)}</div>
                    </div>
                    <StatusChip status={activity.status} />
                  </div>
                ))}
              </div>
            )}
          </ModernCardBody>
        </ModernCard>
      </div>

      {/* Profile Quick Access */}
      <ModernCard className="mt-8 hover-glow">
        <ModernCardHeader title="Profile" icon={UserIcon} gradient="indigo" />
        <ModernCardBody>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <UserIcon className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-white truncate">{user?.full_name || 'Employee'}</h3>
                {/* #4837: user.role is the permission bucket (admin/manager/dispatcher all read ADMIN);
                    db_role is the real user_role_enum value, set by both login paths in UserContext. */}
                <div className="text-sm text-gray-300 truncate"><span>{getRoleDisplayName(user?.db_role || user?.role)}</span> • {user?.email}</div>
              </div>
            </div>
            <button
              onClick={() => navigate('/profile?edit=1')}
              className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex-shrink-0"
            >
              Edit Profile
            </button>
          </div>
        </ModernCardBody>
      </ModernCard>
    </div>
  );
}
